import {
  AI_HINT_RE,
  BRACKETED_CODE_RE,
  ChecklistDisplayRow,
  ChecklistDisplayToken,
  ChecklistTokenKind,
  CONTRACT_BUCKET_RE,
  COUNT_ONLY_RE,
  DocumentTemplate,
  MALFORMED_COUNT_PREFIX_RE,
  MULTI_UNDERSCORE_RE,
  PLACEHOLDER_ANGLE_RE,
  PLACEHOLDER_BRACES_RE,
  SECTION_COUNT_SUFFIX_RE,
  SectionLineSplit,
  SINGLE_UNDERSCORE_RE,
  STRIP_INDEX_RE,
  TERM_SPLIT_RE,
} from '../config';

import {
  countChecklistOptions,
  extractChecklistFieldDescriptors,
  formatChecklistSectionDisplayLabel,
  getChecklistSectionOptionCount,
  shouldDisplayChecklistSection,
  stripSingleOptionCountFromLabel,
} from './checklistSectionRules';

const deduplicate = (items: string[]): string[] => [
  ...new Set(items.map((item) => item.trim()).filter(Boolean)),
];

const normalizeLookup = (value: string): string =>
  value.toLocaleLowerCase().replace(/\s+/g, ' ').trim();

const trimTrailingDelimiters = (value: string): string =>
  value
    .trim()
    .replace(/[;:]+$/g, '')
    .trim();

const normalizeTokenLabel = (raw: string): string => {
  const cleaned = raw
    .trim()
    .replace(/^_+/, '')
    .replace(/^(__)?ai_/, '')
    .replace(/^unchanged_existing_/, '')
    .replace(/_from_template$/, '')
    .replace(/_field_value$/, '')
    .replace(/_option_value$/, '')
    .replace(/[[\]{}]/g, '')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return trimTrailingDelimiters(cleaned);
};

const parseCompactTerms = (raw: string): string[] =>
  raw
    .split(TERM_SPLIT_RE)
    .map((term) =>
      term
        .trim()
        .replace(/[[\]{}()]/g, '')
        .replace(/\s+/g, ' '),
    )
    .filter((term) => term.length > 0);

const deduplicateTokens = (
  tokens: ChecklistDisplayToken[],
): ChecklistDisplayToken[] =>
  tokens.reduce<ChecklistDisplayToken[]>((result, token) => {
    const normalizedValue = normalizeTokenLabel(token.value);
    if (!normalizedValue) {
      return result;
    }

    const previous = result[result.length - 1];
    if (
      previous &&
      normalizeLookup(previous.value) === normalizeLookup(normalizedValue) &&
      previous.kind === token.kind
    ) {
      return result;
    }

    return [...result, { kind: token.kind, value: normalizedValue }];
  }, []);

const tokenizeValue = (value: string): ChecklistDisplayToken[] => {
  const trimmed = value.trim();
  if (!trimmed) {
    return [];
  }

  const bucketMatches = [...trimmed.matchAll(CONTRACT_BUCKET_RE)];
  if (bucketMatches.length > 0) {
    const bucketTokens = bucketMatches.flatMap((match) => {
      const kind: ChecklistTokenKind =
        match[1]?.toUpperCase() === 'AI' ? 'ai' : 'predefined';
      return parseCompactTerms(match[2] ?? '').map((bucketValue) => ({
        kind,
        value: bucketValue,
      }));
    });
    return deduplicateTokens(bucketTokens);
  }

  const normalized = trimmed
    .replace(PLACEHOLDER_BRACES_RE, (_, name: string) =>
      normalizeTokenLabel(name),
    )
    .replace(PLACEHOLDER_ANGLE_RE, (_, name: string) =>
      normalizeTokenLabel(name),
    )
    .replace(BRACKETED_CODE_RE, '')
    .replace(MULTI_UNDERSCORE_RE, '')
    .replace(SINGLE_UNDERSCORE_RE, '$1')
    .replace(/[{}]/g, '');

  const fallbackTokens = parseCompactTerms(normalized).map(
    (tokenValue): ChecklistDisplayToken => ({
      kind: AI_HINT_RE.test(tokenValue) ? 'ai' : 'predefined',
      value: tokenValue,
    }),
  );

  return deduplicateTokens(fallbackTokens);
};

const looksLikeTemplateName = (item: string): boolean => {
  if (!item.trim()) {
    return false;
  }
  if (item.includes(':') || item.includes('{{') || item.includes('<')) {
    return false;
  }
  return item.split(/\s+/).length >= 2;
};

const normalizeSectionLookupValue = (value: string): string =>
  normalizeLookup(value.replace(SECTION_COUNT_SUFFIX_RE, '').trim());

const splitSectionLine = (item: string): SectionLineSplit => {
  const separatorIndex = item.indexOf(':');
  if (separatorIndex < 0) {
    return { label: item.trim(), rawValue: '' };
  }

  const label = trimTrailingDelimiters(item.slice(0, separatorIndex).trim());
  const rawValue = item.slice(separatorIndex + 1).trim();

  const countOnlyMatch = rawValue.match(COUNT_ONLY_RE);
  if (countOnlyMatch) {
    return { label: `${label} (${countOnlyMatch[1]})`, rawValue: '' };
  }

  const malformedCountMatch = rawValue.match(MALFORMED_COUNT_PREFIX_RE);
  if (malformedCountMatch) {
    return {
      label: `${label} (${malformedCountMatch[1]})`,
      rawValue: malformedCountMatch[2]?.trim() ?? '',
    };
  }

  return { label, rawValue };
};

const parseRow = (item: string): ChecklistDisplayRow => {
  const cleaned = item.replace(STRIP_INDEX_RE, '').trim();
  const { label, rawValue } = splitSectionLine(cleaned);
  const sectionAliases = [
    normalizeSectionLookupValue(label),
    ...label
      .split('/')
      .map((part) => normalizeSectionLookupValue(part))
      .filter((part) => part.length > 0),
  ];
  const valueTokens = deduplicateTokens(
    tokenizeValue(rawValue).filter(
      (token) => !sectionAliases.includes(normalizeLookup(token.value)),
    ),
  );

  return { key: normalizeLookup(cleaned), label, valueTokens };
};

export const hasCompletedChecklist = (
  template: Pick<DocumentTemplate, 'checklist'> | null | undefined,
): boolean =>
  template?.checklist?.status === 'completed' &&
  (template.checklist.content?.items?.length ?? 0) > 0;

export const buildChecklistRows = (
  template: DocumentTemplate,
): ChecklistDisplayRow[] => {
  if (!hasCompletedChecklist(template)) {
    return [];
  }

  const uniqueItems = deduplicate(template.checklist?.content?.items ?? []);
  const displayItems =
    uniqueItems.length > 1 && looksLikeTemplateName(uniqueItems[0])
      ? uniqueItems.slice(1)
      : uniqueItems;

  const parsedRows = displayItems
    .map(parseRow)
    .filter((row) => row.label.length > 0);

  const parsedByLabel = parsedRows.reduce(
    (map, row) => {
      const lookup = normalizeSectionLookupValue(row.label);
      return map[lookup] ? map : { ...map, [lookup]: row };
    },
    {} as Record<string, ChecklistDisplayRow>,
  );

  const sections = (template.sections ?? []).filter((section) =>
    Boolean(section.Title.trim()),
  );
  const sectionLookups = new Set(
    sections.map((section) => normalizeSectionLookupValue(section.Title)),
  );

  const templateOrderedRows = sections.reduce<ChecklistDisplayRow[]>(
    (rows, section) => {
      const sectionTitle = section.Title.trim();
      const sectionLookup = normalizeSectionLookupValue(sectionTitle);
      const optionCount = countChecklistOptions(section.Options);
      const canonicalValueTokens: ChecklistDisplayToken[] =
        extractChecklistFieldDescriptors(
          section.Options,
          template.language,
        ).map((descriptor) => ({
          kind: descriptor.kind,
          value: descriptor.label,
        }));

      if (
        !shouldDisplayChecklistSection(optionCount, canonicalValueTokens.length)
      ) {
        return rows;
      }

      const displayLabel = stripSingleOptionCountFromLabel(
        formatChecklistSectionDisplayLabel(sectionTitle, optionCount),
      );
      const parsed = parsedByLabel[sectionLookup];

      if (parsed) {
        return [
          ...rows,
          {
            key: sectionLookup,
            label: displayLabel,
            valueTokens:
              canonicalValueTokens.length > parsed.valueTokens.length
                ? canonicalValueTokens
                : parsed.valueTokens.length > 0
                  ? parsed.valueTokens
                  : canonicalValueTokens,
          },
        ];
      }

      return [
        ...rows,
        {
          key: sectionLookup,
          label: displayLabel,
          valueTokens: canonicalValueTokens,
        },
      ];
    },
    [],
  );

  const extraParsedRows = parsedRows
    .filter((row) => {
      if (sectionLookups.has(normalizeSectionLookupValue(row.label))) {
        return false;
      }
      const optionCount = getChecklistSectionOptionCount(row.label);
      if (optionCount === null) {
        return row.valueTokens.length > 0;
      }
      return shouldDisplayChecklistSection(optionCount, row.valueTokens.length);
    })
    .map((row) => ({
      ...row,
      label: stripSingleOptionCountFromLabel(row.label),
    }));

  return [...templateOrderedRows, ...extraParsedRows];
};
